import {
  Flex,
  IconCertifiedLine,
  IconRefreshLine,
  Spinner,
  Text,
  Tooltip,
} from "@instructure/ui";

import { useContext, useState } from "react";
import "./../curriculum-map.css";

import MappingDetailsModal from "./MappingDetailsModal";

import { useTranslation } from "react-i18next";
import MappedIcon from "../../../CustomIcons/Mapped";
import MappedAssessmentIcon from "../../../CustomIcons/MappedAssessment";
import MappedCurrentAssessmentIcon from "../../../CustomIcons/MappedCurrentAssessment";
import { AttainmentRuleService } from "../../../services/AttainmentRuleService";
import { MappingsService } from "../../../services/MappingService";
import { UserDetailContext } from "../../../utils/Context";
import { ERROR, useNotification } from "../../CustomHooks/NotificationProvider";
import { isArchivedByDate } from "../../LearningOutcomes/utils";

const PLAN_ASSOCIATED = "planAssociated";
const PLAN_NOT_ASSOCIATED = "planNotAssociated";
const CURRENT_PLAN = "currentPlan";

const MappingButton = ({
  mapping,
  row,
  column,
  dash,
  badgeMappings,
  date,
  selectedAssessmentPlan,
  learningOutcomeType,
  attainments,
  setBadgeMappings,
  futureMap,
  rows,
  coursesWithOutcomes,
  editable,
}) => {
  const { t } = useTranslation();
  const userDetails = useContext(UserDetailContext);
  const showNotification = useNotification();
  const [currentMapping, setCurrentMapping] = useState(mapping);
  const [openModal, setOpenModal] = useState(false);
  const [isBeingDeleted, setIsBeingDeleted] = useState(false);
  const [isBeingCreated, setIsBeingCreated] = useState(false);

  const openEditMappingModal = (oldMapping, row, column) => {
    const mappingToEdit = oldMapping
      ? { ...oldMapping }
      : { from: row.course.id, to: column.key };
    mappingToEdit.courseName = row.course.code || row.course.statement;
    mappingToEdit.loName = column.unformatted.name;
    mappingToEdit.badgeMapping = badgeMappings.find(
      (badgeMapping) =>
        badgeMapping.sourceOutcomeId === row.course.id &&
        badgeMapping.targetOutcomeId === column.key &&
        badgeMapping.lifeCycle === (futureMap ? "future" : "current")
    );
    mappingToEdit.sourceVersions = row.versions;
    mappingToEdit.targetVersions = column.versions;
    mappingToEdit.is_default = row.course.is_default;

    return mappingToEdit;
  };

  const checkCurrentPlan = (currentMapping, row) => {
    for (const section of selectedAssessmentPlan.sections) {
      for (const assessment of section.assessments) {
        if (assessment.los) {
          for (const lo of assessment.los) {
            if (lo.id === currentMapping.to) {
              for (const offering of assessment.offerings) {
                if (
                  offering.id in assessment.offering_to_terms_map &&
                  row.course.id === offering.id
                ) {
                  for (const mapping of offering.mappings) {
                    if (mapping.id === currentMapping.id) {
                      return true;
                    }
                  }
                } else if (
                  offering.id in assessment.offering_to_terms_map &&
                  offering.learning_outcomes
                    .map((outcome) => outcome.id)
                    .includes(row.course.id)
                ) {
                  for (const outcome of offering.learning_outcomes) {
                    for (const mapping of outcome.mappings) {
                      if (mapping.id === currentMapping.id) {
                        return true;
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    return false;
  };

  const handleMappingStatus = (mapping, row) => {
    if (!dash || userDetails.isFaculty()) {
      return mapping.planned ? PLAN_ASSOCIATED : PLAN_NOT_ASSOCIATED;
    } else {
      if (!mapping.planned) {
        return PLAN_NOT_ASSOCIATED;
      } else {
        return checkCurrentPlan(mapping, row) ? CURRENT_PLAN : PLAN_ASSOCIATED;
      }
    }
  };

  const mappingPermissions = (row, column) => {
    return (
      !isArchivedByDate(row.course, date) &&
      !isArchivedByDate(column.unformatted, date) &&
      (userDetails.isAC() ||
        (userDetails.isPC() &&
          (learningOutcomeType === "pslo" ||
            (learningOutcomeType === "islo" &&
              userDetails.ac_subjects.includes(row.ac_sub)))))
    );
  };

  const handleAssignmentOptionsCourse = (modalData) => {
    if (modalData?.properties?.assignmentTypes) {
      if (typeof modalData.properties.assignmentTypes === "string") {
        return modalData.properties.assignmentTypes.split(",");
      } else if (typeof modalData.properties.assignmentTypes === "object") {
        return modalData.properties.assignmentTypes;
      }
    }
    return [];
  };

  const handleAssignmentOptionsIloPlo = (modalData) => {
    if (
      modalData?.properties?.iloAssignmentTypes ||
      modalData?.properties?.ploAssignmentTypes
    ) {
      if (
        modalData.properties.iloAssignmentTypes &&
        typeof modalData.properties.iloAssignmentTypes === "string"
      ) {
        return modalData.properties.iloAssignmentTypes.split(",");
      } else if (
        modalData.properties.iloAssignmentTypes &&
        typeof modalData.properties.iloAssignmentTypes === "object"
      ) {
        return modalData.properties.iloAssignmentTypes;
      } else if (
        modalData.properties.ploAssignmentTypes &&
        typeof modalData.properties.ploAssignmentTypes === "string"
      ) {
        return modalData.properties.ploAssignmentTypes.split(",");
      } else if (
        modalData.properties.ploAssignmentTypes &&
        typeof modalData.properties.ploAssignmentTypes === "object"
      ) {
        return modalData.properties.ploAssignmentTypes;
      }
    }
    return [];
  };

  const mappingCombos = (modalData) => {
    const futureVersionList = [];
    if (modalData.sourceVersions) {
      modalData.sourceVersions.forEach((sourceVersion) => {
        modalData.targetVersions.forEach((targetVersion) => {
          if (
            sourceVersion.id !== modalData.from ||
            targetVersion.id !== modalData.to
          ) {
            futureVersionList.push({
              originId: sourceVersion.id,
              destinationId: targetVersion.id,
            });
          }
        });
      });
    } else {
      modalData.targetVersions.forEach((targetVersion) => {
        if (targetVersion.id !== modalData.to) {
          futureVersionList.push({
            originId: modalData.from,
            destinationId: targetVersion.id,
          });
        }
      });
    }
    return futureVersionList;
  };

  const saveMapping = async (e, modalData) => {
    try {
      if (e != null) {
        e.preventDefault();
      }
      const upcomingVersions = mappingCombos(modalData);
      const mappingPromise = modalData.id
        ? MappingsService.updateMapping(
            modalData.id,
            modalData.from,
            modalData.to,
            modalData.properties?.degreesOfLearning ?? null,
            modalData.properties?.assignmentTypes ?? null,
            modalData.properties?.ploAssignmentTypes ?? null,
            modalData.properties?.iloAssignmentTypes ?? null,
            upcomingVersions,
            futureMap,
            modalData.is_default,
            !!modalData.properties?.lo_sync
          )
        : MappingsService.addMapping(
            modalData.from,
            modalData.to,
            modalData.properties?.degreesOfLearning ?? null,
            modalData.properties?.assignmentTypes ?? null,
            modalData.properties?.ploAssignmentTypes ?? null,
            modalData.properties?.iloAssignmentTypes ?? null,
            upcomingVersions,
            futureMap,
            modalData.is_default,
            !!modalData.properties?.lo_sync
          );

      const res = await mappingPromise;

      const courseOrOutcome = rows.filter(
        (row) => row.course.id === res.origin_id
      )[0].course;
      const newMapping = {
        id: res.id,
        from: res.origin_id,
        to: res.destination_id,
        properties: {
          degreesOfLearning: res.degrees_of_learning,
          assignmentTypes: res.clo_assignment_types,
          ploAssignmentTypes: res.plo_assignment_types ?? null,
          iloAssignmentTypes: res.ilo_assignment_types ?? null,
          lo_sync: !!res.lo_sync,
        },
      };

      const oldMappings = courseOrOutcome.mappings.filter(
        (mapping) => mapping.id === modalData.id
      );

      if (oldMappings.length > 0) {
        courseOrOutcome.mappings = courseOrOutcome.mappings.map((oldMapping) =>
          oldMapping.id !== newMapping.id ? oldMapping : newMapping
        );
      } else {
        courseOrOutcome.mappings.push(newMapping);
      }

      setCurrentMapping(newMapping);
      setOpenModal(false);
      showNotification(t("notifications.curriculumMap.updateSuccess"));
    } catch {
      showNotification(t("notifications.curriculumMap.updateError"), ERROR);
    } finally {
      setIsBeingCreated(false);
    }
  };

  const deleteMapping = async (modalData, deleteFutureMappings) => {
    const upcomingVersions = mappingCombos(modalData);
    const promises = [];

    if (modalData.id) {
      promises.push(
        MappingsService.deleteMapping(
          modalData.id,
          upcomingVersions,
          deleteFutureMappings,
          !!futureMap,
          modalData.is_default
        )
      );
    }

    if (modalData.badgeMapping) {
      promises.push(
        AttainmentRuleService.deleteBadgeMapping(modalData.badgeMapping.id)
      );
    }

    try {
      await Promise.all(promises);

      if (modalData.id) {
        const courseOrOutcome = rows.filter(
          (row) => row.course.id === modalData.from
        )[0].course;
        courseOrOutcome.mappings = courseOrOutcome.mappings.filter(
          (mapping) => mapping.id !== modalData.id
        );
        setCurrentMapping(null);
      }
      if (modalData.badgeMapping) {
        setBadgeMappings((prevMappings) =>
          prevMappings.filter(
            (mapping) => mapping.id !== modalData.badgeMapping.id
          )
        );
      }
      setOpenModal(false);
      showNotification(t("notifications.curriculumMap.deleteSuccess"));
    } catch {
      showNotification(t("notifications.curriculumMap.deleteError"), ERROR);
    } finally {
      setIsBeingDeleted(false);
    }
  };

  const updateRatingScale = (rubricId, modalData) => {
    const courseOrOutcome = rows.filter(
      (row) => row.course.id === modalData.from
    )[0].course;
    courseOrOutcome.rating_to_mastery_level_map.id = rubricId;
  };

  let curData = openEditMappingModal(currentMapping, row, column);

  const onClose = (event) => {
    if (event != null) {
      event.stopPropagation();
    }
    setOpenModal(false);
  };

  const elementKey = currentMapping
    ? currentMapping.id
    : `${row.course.id}_${column.key}`;
  let mappingStatus = "";
  if (currentMapping) {
    mappingStatus = handleMappingStatus(currentMapping, row);
  }

  return (
    <>
      <Tooltip
        color="primary-inverse"
        on={["hover", "focus"]}
        isShowingContent={openModal ? false : undefined}
        placement="start"
        renderTip={
          mappingStatus === CURRENT_PLAN
            ? t("curriculumMap.currentPlanTooltip")
            : mappingStatus === PLAN_ASSOCIATED
              ? !dash || userDetails.isFaculty()
                ? t("curriculumMap.anyPlanTooltip")
                : t("curriculumMap.differentPlanTooltip")
              : mappingStatus === PLAN_NOT_ASSOCIATED
                ? t("curriculumMap.noPlanTooltip")
                : t("curriculumMap.noMapping")
        }
      >
        {currentMapping ? (
          <Flex wrap="wrap">
            <Flex.Item align="center" size="100%">
              <button
                id={"button-" + elementKey}
                aria-label={t("curriculumMap.mapping")}
                className={`${isBeingDeleted ? "toDelete" : ""}`}
                style={{
                  border: "none",
                  cursor: "pointer",
                  width: "100%",
                  height:
                    currentMapping.properties.degreesOfLearning ||
                    (!dash && mappingPermissions(row, column))
                      ? 100
                      : 120,
                  backgroundColor:
                    dash && mappingStatus === CURRENT_PLAN
                      ? "#D4AF37"
                      : "#0B874C",
                  textAlign: "center",
                  padding: "10% 0",
                }}
                onClick={() => {
                  if (!dash && editable && mappingPermissions(row, column)) {
                    setIsBeingDeleted(true);
                    if (!isBeingDeleted) {
                      deleteMapping(curData, !!futureMap);
                    }
                  }
                }}
              >
                {mappingStatus === CURRENT_PLAN ? (
                  <MappedCurrentAssessmentIcon size="48px" color="#fff" />
                ) : mappingStatus === PLAN_ASSOCIATED ? (
                  <MappedAssessmentIcon size="48px" color="#fff" />
                ) : (
                  <MappedIcon size="48px" color="#fff" />
                )}
              </button>
            </Flex.Item>
            <Flex.Item size="100%" align="center">
              <button
                data-test-column={column.unformatted.name}
                data-test-row={row.course.code || row.course.name}
                tabIndex={0}
                aria-label={t("curriculumMap.editMapping")}
                onClick={() => {
                  if (!isBeingDeleted) {
                    curData = openEditMappingModal(currentMapping, row, column);
                    setOpenModal(true);
                  }
                }}
                className={`${isBeingDeleted ? "toDelete" : ""} ${
                  currentMapping.properties.degreesOfLearning ||
                  (!dash && mappingPermissions(row, column))
                    ? mappingStatus === CURRENT_PLAN
                      ? !isBeingDeleted && "currentLowerButton"
                      : !isBeingDeleted && "otherLowerButton"
                    : "hidden"
                }`}
                style={{
                  border: "none",
                  cursor: "pointer",
                  color: "#fff",
                  width: "100%",
                  height: 20,
                  textAlign: "center",
                  fontWeight: "bold",
                  backgroundColor:
                    dash && mappingStatus === CURRENT_PLAN
                      ? "#8F6F1E"
                      : "darkgreen",
                }}
              >
                {(currentMapping.properties.degreesOfLearning ||
                  (!dash && mappingPermissions(row, column))) && (
                  <Text size="x-small" as="p">
                    {currentMapping.properties.degreesOfLearning
                      ? currentMapping.properties.degreesOfLearning
                      : t("attainmentLevel.reviewMappingSettings")}
                  </Text>
                )}
              </button>
              {curData.properties.lo_sync && (
                <div
                  style={{
                    position: "absolute",
                    right: curData.badgeMapping ? "35px" : "5px",
                    top: "5px",
                    color: "#fff",
                    fontSize: "1.5em",
                  }}
                >
                  <Tooltip
                    color="primary-inverse"
                    renderTip={"Auto-sync enabled"}
                    placement="top"
                    on={["hover", "focus"]}
                  >
                    <IconRefreshLine />
                  </Tooltip>
                </div>
              )}
              {curData.badgeMapping && (
                <div
                  style={{
                    position: "absolute",
                    right: "5px",
                    top: "5px",
                    color: "#fff",
                    fontSize: "1.5em",
                  }}
                >
                  <Tooltip
                    color="primary-inverse"
                    renderTip={t("badging.rewardedGeneral")}
                    placement="top"
                    on={["hover", "focus"]}
                  >
                    <IconCertifiedLine />
                  </Tooltip>
                </div>
              )}
            </Flex.Item>
            {isBeingDeleted && (
              <div className="absolutePos">
                <Spinner
                  size="small"
                  renderTitle={t("curriculumMap.deleteSpinner")}
                  themeOverride={{
                    trackColor: "transparent",
                    color: "#20627f",
                  }}
                />
              </div>
            )}
          </Flex>
        ) : (
          <button
            tabIndex={0}
            key={"button-" + elementKey}
            aria-label={t("curriculumMap.addMapping")}
            style={{
              border: "none",
              cursor: "pointer",
              width: "100%",
              height: mapping ? 100 : 120,
              backgroundColor: "transparent",
            }}
            onClick={() => {
              if (!dash && editable && mappingPermissions(row, column)) {
                curData = openEditMappingModal(currentMapping, row, column);
                setIsBeingCreated(true);
                if (!isBeingCreated) {
                  saveMapping(null, curData);
                }
              }
            }}
          >
            {isBeingCreated && (
              <Spinner
                size="small"
                renderTitle={t("curriculumMap.saveSpinner")}
                themeOverride={{ trackColor: "#20627f", color: "white" }}
              />
            )}
          </button>
        )}
      </Tooltip>
      <MappingDetailsModal
        openModal={openModal && !dash && mappingPermissions(row, column)}
        modalData={JSON.parse(JSON.stringify(curData))}
        outcome={row.course}
        handleCloseModal={(e) => onClose(e)}
        attainments={attainments}
        assignmentTypeCourse={handleAssignmentOptionsCourse(curData)}
        assignmentTypeIloPlo={handleAssignmentOptionsIloPlo(curData)}
        deleteMapping={deleteMapping}
        saveMapping={saveMapping}
        learningOutcomeType={learningOutcomeType}
        coursesWithOutcomes={coursesWithOutcomes}
        dash={dash}
        updateRatingScale={updateRatingScale}
        setBadgeMappings={setBadgeMappings}
        futureMap={futureMap}
      />
    </>
  );
};

export default MappingButton;
